/**
 * @file Stream an export bundle (data.json + schema.md + README.md + media/)
 * to a user-chosen file using the `client-zip` library and the File System
 * Access API (showSaveFilePicker).
 *
 * Browsers without showSaveFilePicker (Firefox at the time of writing) get a
 * `BrowserUnsupportedError` so the caller can show a clean message instead of
 * silently OOM'ing on a large in-memory blob.
 */

import { downloadZip } from "client-zip";

export class BrowserUnsupportedError extends Error {
  constructor() {
    super("File System Access API (showSaveFilePicker) is not available");
    this.name = "BrowserUnsupportedError";
  }
}

export interface ZipEntry {
  /** Path inside the zip, e.g. "data.json" or "media/entry_42_0.jpg". */
  name: string;
  input: Uint8Array | string;
  /** Optional last-modified date stamped into the zip (UTC). */
  lastModified?: Date;
}

declare global {
  interface Window {
    showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>;
  }
  interface SaveFilePickerOptions {
    suggestedName?: string;
    types?: Array<{
      description?: string;
      accept?: Record<string, string[]>;
    }>;
  }
  interface FileSystemFileHandle {
    createWritable(): Promise<FileSystemWritableFileStream>;
  }
  interface FileSystemWritableFileStream extends WritableStream<Uint8Array> {
    close(): Promise<void>;
  }
}

/**
 * Open a save dialog and return a writable handle; throws
 * [BrowserUnsupportedError] on browsers that don't support the API.
 */
export async function openSaveFile(suggestedName: string): Promise<FileSystemFileHandle> {
  if (typeof window === "undefined" || !window.showSaveFilePicker) {
    throw new BrowserUnsupportedError();
  }
  return window.showSaveFilePicker({
    suggestedName,
    types: [
      {
        description: "Berry Buds export",
        accept: { "application/zip": [".zip"] },
      },
    ],
  });
}

/**
 * Pipe an async iterable of [ZipEntry] into the user-selected file, streaming
 * one entry at a time so memory peak ≈ 1 file's worth. Returns the total
 * bytes written.
 */
export async function streamZipToFile(
  handle: FileSystemFileHandle,
  entries: AsyncIterable<ZipEntry>,
): Promise<number> {
  const response = downloadZip(adaptToClientZip(entries));
  const body = response.body;
  if (!body) {
    throw new Error("client-zip returned a Response without a body");
  }
  const writable = await handle.createWritable();
  let written = 0;
  const counter = new TransformStream<Uint8Array, Uint8Array>({
    transform(chunk, controller) {
      written += chunk.byteLength;
      controller.enqueue(chunk);
    },
  });
  await body.pipeThrough(counter).pipeTo(writable);
  return written;
}

async function* adaptToClientZip(entries: AsyncIterable<ZipEntry>): AsyncGenerator<{
  name: string;
  input: Uint8Array | string;
  lastModified?: Date;
}> {
  for await (const e of entries) {
    yield e;
  }
}
